import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Grid, Stack, SvgIcon, TextField, useMediaQuery, useTheme } from '@mui/material';
import { Plus, SearchSm } from '../../../assets';
import useLogError from '../../../hooks/useLogError';
import { SuccessResponse_1OfUserListResponse, User } from '../../../app/services/api/generated';
import useNswagClient from '../../../hooks/api/useNswagClient';
import PageTitle from '../../../components/shared/PageTitle';
import PageArea from '../../../components/shared/PageArea';
import { Section } from '../../../components/ui/Section';
import UsersTable from './components/UsersTable';
import { UpsertUserRequest } from './types/UpsertUserRequest';
import CreateUpdateUserModal from './components/CreateUpdateUserModal';
import MessagesSnackbar from '../../../components/shared/MessagesSnackbar';
import { UserContext } from '../../../components/shared/useUser';

const ManageUsers = () => {
  const { getUsers } = useNswagClient();
  const { t } = useTranslation('manageUsers');
  const [users, setUsers] = useState<User[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [companyFilter, setCompanyFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const { createUser } = useNswagClient();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { logError } = useLogError();
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [transformedData, setTransformedData] = useState<User[]>(users);
  const [page, setPage] = useState(0);
  const [showCreateSuccessMessage, setShowCreateSuccessMessage] = useState<boolean>(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(`(max-width:${theme.breakpoints.values.md}px)`);
  const { hasPermissionTo } = useContext(UserContext);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    setTransformedData(users);
  }, [users]);

  const loadData = () => {
    setIsLoading(true);

    getUsers()
      .then((result: SuccessResponse_1OfUserListResponse) => {
        if (result?.data) {
          setUsers(result.data?.users ?? []);
        }
      })
      .catch((error) => logError(error))
      .finally(() => setIsLoading(false));
  };

  const handleCreate = (body: UpsertUserRequest) => {
    createUser(body)
      .then(() => {
        setShowCreateSuccessMessage(true);
        loadData();
      })
      .catch(error => logError(error));

  };
  
  const handleSearch = () => {
    setPage(0);

    let filteredData = [...users];

    if (searchTerm !== '') {
      filteredData = filteredData.filter(
        x => x.emailAddress?.toLowerCase().includes(searchTerm.toLowerCase()));
    }
    if (companyFilter !== '') {
      filteredData = filteredData.filter(
        x => x.franchiseName?.toLowerCase().includes(companyFilter.toLowerCase()));
    }
    if (roleFilter !== '') {
      filteredData = filteredData.filter(
        x => x.persona?.description?.toLowerCase().includes(roleFilter.toLowerCase()));
    }

    setTransformedData(filteredData);
  };

  const handleClear = () => {
    setPage(0);
    setSearchTerm('');
    setCompanyFilter('');
    setRoleFilter('');
    loadData();
  };

  return (
    <PageArea>
      <Box
        display='flex'
        width='100%'
        height='100%'
        flexDirection='column'
      >
        <Grid container>
          <Stack
            width='100%'
            spacing={2}
            direction={isMobile ? 'column' : 'row'}
            sx={{
              justifyContent: 'space-between',
            }}
          >
            <Box width={isMobile ? '100%' : 'auto'}>
              <PageTitle subTitle={t('manageUsers')}>{t('users')}</PageTitle>
            </Box>
            <Box width={isMobile ? '100%' : 'auto'}
              sx={{
                display: 'flex',
                justifyContent: { xs: 'flex-start', md: 'flex-end' },
              }}
            >
              <Button
                variant="primary"
                size="lg"
                sx={{
                  mb: { xs: 4, md: 0 },
                }}
                disabled={!hasPermissionTo(['UserManagementCreate'])}
                onClick={() => setShowCreateModal(true)}
                startIcon={<SvgIcon><Plus fill="white" /></SvgIcon>}
              >
                {t('newUser')}
              </Button>
            </Box>
          </Stack>

          <Grid container
            spacing={2}>
            <Grid container
              sx={{ pt: '6px', pb: '12px' }}
              spacing={4}>
              <Grid item
                lg={2}
                sm={4}
                xs={12}>
                <TextField
                  fullWidth
                  value={searchTerm}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      handleSearch();
                    }
                  }}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  size='small'
                  label={t('name')}
                />
              </Grid>
              <Grid item
                lg={2}
                sm={4}
                xs={12}>
                <TextField
                  fullWidth
                  value={companyFilter}
                  onChange={(event) => setCompanyFilter(event.target.value)}
                  size='small'
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      handleSearch();
                    }
                  }}
                  label={t('company')}
                />
              </Grid>
              <Grid item
                lg={2}
                sm={4}
                xs={12}>
                <TextField
                  fullWidth
                  value={roleFilter}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      handleSearch();
                    }
                  }}
                  onChange={(event) => setRoleFilter(event.target.value)}
                  size='small'
                  label={t('role')}
                />
              </Grid>

              <Grid item
                xs={12}
                sm={2}
                lg={2}
                sx={{ maxWidth: 'fit-content', mr: 2 }}>
                <Button
                  fullWidth
                  variant="secondary"
                  size="lg"
                  onClick={() => handleClear()}
                >
                  {t('clearFilters')}
                </Button>
              </Grid>
              <Grid item
                lg={2}
                sm={2}
                xs={12}
                sx={{ maxWidth: 'fit-content' }}>
                <Button
                  fullWidth
                  variant="primary"
                  size="lg"
                  onClick={handleSearch}
                  startIcon={<SvgIcon> <SearchSm /> </SvgIcon>}
                >
                  {t('search')}
                </Button>
              </Grid>
            </Grid>
          </Grid>

          <Grid item
            xs={12}
            sx={{ mt: '12px' }}>
            {transformedData &&
              <Section>
                <UsersTable
                  list={transformedData}
                  setPage={setPage}
                  clearAll={handleClear}
                  searchTerm=''
                  page={page}
                  loadData={loadData}
                  isLoading={isLoading}
                  onCreate={() => setShowCreateModal(true)}
                />
              </Section>
            }
          </Grid>
        </Grid>
        <CreateUpdateUserModal
          openModal={showCreateModal}
          onConfirm={handleCreate}
          editUser={undefined}
          onCancel={() => setShowCreateModal(false)}
          onResetPassword={() => { }}
        />
        <MessagesSnackbar
          open={showCreateSuccessMessage}
          onClose={() => setShowCreateSuccessMessage(false)}
          message={t('successCreateMessage')}
          severity="success"
          duration={null}
        />
      </Box>
    </PageArea>
  );
};

export default ManageUsers;